import { HTMLAttributes } from 'react';

import { FontSize, FontWeight } from '../../utils/typography';
import { PaletteColor } from '../ThemeProvider';

export const HeaderElements = {
	H1: 'h1',
	H2: 'h2',
	H3: 'h3',
	H4: 'h4',
	H5: 'h5',
	H6: 'h6',
} as const;
export type HeaderElement = (typeof HeaderElements)[keyof typeof HeaderElements];

export const TextElements = {
	Div: 'div',
	Span: 'span',
	P: 'p',
	Label: 'label',
	Li: 'li',
	Legend: 'legend',
	DescriptionTerm: 'dt',
	DescriptionDetails: 'dd',
	...HeaderElements,
} as const;
export type TextElement = (typeof TextElements)[keyof typeof TextElements];

export const TextAlign = {
	Left: 'left',
	Center: 'center',
	Right: 'right',
	Start: 'start',
	End: 'end',
	Justify: 'justify',
	JustifyAll: 'justify-all',
	MatchParent: 'match-parent',
} as const;
export type TextAlignment = (typeof TextAlign)[keyof typeof TextAlign];

export const TextVerticalAligns = {
	Baseline: 'baseline',
	Sub: 'sub',
	Super: 'super',
	TextTop: 'text-top',
	TextBottom: 'text-bottom',
	Middle: 'middle',
	Top: 'top',
	Bottom: 'bottom',
} as const;
export type TextVerticalAlignment = (typeof TextVerticalAligns)[keyof typeof TextVerticalAligns];

export interface TextBaseProps {
	size?: FontSize;
	weight?: FontWeight;
	align?: TextAlignment;
	verticalAlign?: TextVerticalAlignment;
	color?: PaletteColor;
	opacity?: number;
	lineHeight?: number | string;
	letterSpacing?: string;
	uppercase?: boolean;
	noMargin?: boolean;
	className?: string;
	isHorizontalError?: boolean;
	/**
	 * Applies the default typography styles of the `styleAs` to the `el`.
	 * @example
	 * The following will be rendered as an `h1` but be styled like an `h4`.
	 * ```tsx
	 * <Text el="h1" styleAs="h4">Hello</Text>
	 * ```
	 */
	styleAs?: TextElement;
	/**
	 * Displays only to screen readers (visually hidden)
	 */
	srOnly?: boolean;
	['data-testid']?: string;
}

type TextHeadingProps = HTMLAttributes<HTMLHeadingElement> & {
	el?: HeaderElement;
};

type TextDivProps = HTMLAttributes<HTMLDivElement> & {
	el?: typeof TextElements.Div;
};

type TextSpanProps = HTMLAttributes<HTMLSpanElement> & {
	el?: typeof TextElements.Span;
};

export type TextLabelProps = HTMLAttributes<HTMLLabelElement> & {
	el?: typeof TextElements.Label;
	htmlFor?: string;
};

type TextLiProps = HTMLAttributes<HTMLLIElement> & {
	el?: typeof TextElements.Li;
};

type TextParagraphProps = HTMLAttributes<HTMLParagraphElement> & {
	el?: typeof TextElements.P;
};

type TextLegendProps = HTMLAttributes<HTMLLegendElement> & {
	el?: typeof TextElements.Legend;
};

type TextDescriptionProps = HTMLAttributes<HTMLElement> & {
	el?: typeof TextElements.DescriptionTerm | typeof TextElements.DescriptionDetails;
};

export type TextProps = TextBaseProps &
	(
		| TextHeadingProps
		| TextDivProps
		| TextSpanProps
		| TextLabelProps
		| TextLiProps
		| TextParagraphProps
		| TextLegendProps
		| TextDescriptionProps
	);
